import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
    APP_STORE_ID,
    DEFAULT_DESCRIPTION,
    DEFAULT_OG_IMAGE,
    DEFAULT_ROBOTS,
    ORGANIZATION_STRUCTURED_DATA,
    SITE_NAME,
    SITE_URL,
    SeoRouteData,
} from './seo.config';

@Injectable({
    providedIn: 'root',
})
export class SeoService {
    private initialized = false;
    private activeRouteSeo: SeoRouteData = {};
    private readonly dynamicStructuredData = new Map<string, Record<string, unknown> | Array<Record<string, unknown>>>();

    constructor(
        private readonly router: Router,
        private readonly meta: Meta,
        private readonly title: Title,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {}

    init(): void {
        if (this.initialized) {
            return;
        }

        this.initialized = true;
        this.applyRouteSeo(this.getRouteSeo(this.router.routerState.snapshot.root));

        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(() => {
                this.dynamicStructuredData.clear();
                this.applyRouteSeo(this.getRouteSeo(this.router.routerState.snapshot.root));
            });
    }

    setDynamicStructuredData(
        key: string,
        schema: Record<string, unknown> | Array<Record<string, unknown>>,
    ): void {
        this.dynamicStructuredData.set(key, schema);
        this.updateStructuredData();
    }

    clearDynamicStructuredData(key: string): void {
        if (this.dynamicStructuredData.delete(key)) {
            this.updateStructuredData();
        }
    }

    private applyRouteSeo(routeSeo: SeoRouteData): void {
        this.activeRouteSeo = routeSeo;

        const description = routeSeo.description ?? DEFAULT_DESCRIPTION;
        const image = this.toAbsoluteUrl(routeSeo.image ?? DEFAULT_OG_IMAGE);
        const robots = routeSeo.robots ?? DEFAULT_ROBOTS;
        const canonicalUrl = this.buildCanonicalUrl(routeSeo.canonicalPath);
        const pageTitle = this.buildTitle(routeSeo.title);

        this.title.setTitle(pageTitle);

        this.updateNameTag('description', description);
        this.updateNameTag('robots', robots);
        this.updateNameTag('twitter:card', 'summary_large_image');
        this.updateNameTag('twitter:title', pageTitle);
        this.updateNameTag('twitter:description', description);
        this.updateNameTag('twitter:image', image);
        this.updateNameTag('application-name', SITE_NAME);

        this.updatePropertyTag('og:title', pageTitle);
        this.updatePropertyTag('og:description', description);
        this.updatePropertyTag('og:image', image);
        this.updatePropertyTag('og:url', canonicalUrl);
        this.updatePropertyTag('og:type', routeSeo.type ?? 'website');
        this.updatePropertyTag('og:site_name', SITE_NAME);
        this.updatePropertyTag('og:locale', 'en_ZA');
        this.updateNameTag('apple-itunes-app', `app-id=${APP_STORE_ID}, app-argument=${canonicalUrl}`);

        this.updateCanonicalLink(canonicalUrl);
        this.updateStructuredData();
    }

    private buildTitle(title?: string): string {
        if (!title) {
            return SITE_NAME;
        }

        return title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    }

    private buildCanonicalUrl(canonicalPath?: string): string {
        const currentPath = this.router.url.split('?')[0].split('#')[0];
        const normalizedPath = canonicalPath ?? currentPath;

        if (!normalizedPath || normalizedPath === '/') {
            return SITE_URL;
        }

        return `${SITE_URL}${normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`}`;
    }

    private getRouteSeo(route: ActivatedRouteSnapshot, collectedSeo: SeoRouteData = {}): SeoRouteData {
        const currentSeo = route.data['seo'] as SeoRouteData | undefined;
        const mergedSeo = currentSeo ? { ...collectedSeo, ...currentSeo } : collectedSeo;

        if (!route.firstChild) {
            return mergedSeo;
        }

        return this.getRouteSeo(route.firstChild, mergedSeo);
    }

    private updateStructuredData(): void {
        this.document.querySelectorAll('script[data-rml-seo="structured-data"]').forEach((script) => script.remove());

        const robots = this.activeRouteSeo.robots ?? DEFAULT_ROBOTS;
        if (robots.includes('noindex')) {
            return;
        }

        const canonicalUrl = this.buildCanonicalUrl(this.activeRouteSeo.canonicalPath);
        const title = this.buildTitle(this.activeRouteSeo.title);
        const description = this.activeRouteSeo.description ?? DEFAULT_DESCRIPTION;

        const schemas = [
            ORGANIZATION_STRUCTURED_DATA,
            {
                '@context': 'https://schema.org',
                '@type': 'WebPage',
                name: title,
                description,
                url: canonicalUrl,
            },
            ...this.toSchemaArray(this.activeRouteSeo.structuredData),
            ...Array.from(this.dynamicStructuredData.values()).flatMap((schema) => this.toSchemaArray(schema)),
        ];

        schemas.forEach((schema) => {
            const script = this.document.createElement('script');
            script.type = 'application/ld+json';
            script.setAttribute('data-rml-seo', 'structured-data');
            script.text = JSON.stringify(schema);
            this.document.head.appendChild(script);
        });
    }

    private toSchemaArray(
        schema?: Record<string, unknown> | Array<Record<string, unknown>>,
    ): Array<Record<string, unknown>> {
        if (!schema) {
            return [];
        }

        return Array.isArray(schema) ? schema : [schema];
    }

    private updateCanonicalLink(url: string): void {
        let link = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');

        if (!link) {
            link = this.document.createElement('link');
            link.setAttribute('rel', 'canonical');
            this.document.head.appendChild(link);
        }

        link.href = url;
    }

    private updateNameTag(name: string, content: string): void {
        this.meta.updateTag({ name, content }, `name="${name}"`);
    }

    private updatePropertyTag(property: string, content: string): void {
        this.meta.updateTag({ property, content }, `property="${property}"`);
    }

    private toAbsoluteUrl(url: string): string {
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }

        return `${SITE_URL}${url.startsWith('/') ? url : `/${url}`}`;
    }
}
